import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowRight } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { getContent } = useSiteContent();
  const [artist, setArtist] = useState(null);
  const [flashDesigns, setFlashDesigns] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [artistsRes, flashRes, testimonialsRes, blogRes] = await Promise.all([
        axios.get(`${API}/artists`),
        axios.get(`${API}/flash`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/blog`),
      ]);

      setArtist(artistsRes.data[0]);
      setFlashDesigns(flashRes.data);
      setTestimonials(testimonialsRes.data);
      setBlogPosts(blogRes.data.slice(0, 2));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  return (
    <div data-testid="home-page" className="tattoo-bg-home min-h-screen">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-8 md:py-20">
        
        {/* Hero Section */}
        <section data-testid="hero-section" className="mb-16 md:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-medium leading-tight mb-4 md:mb-6">
              {getContent('hero_title', 'A quieter way to experience tattooing.')}
            </h1>
            <p className="font-body text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
              {getContent('hero_subtitle', 'Slow, intentional tattooing rooted in trust, presence, and personal storytelling.')}
            </p>
            <div className="mt-8 md:mt-12">
              <Link
                to="/quote-estimator"
                data-testid="hero-cta-button"
                className="inline-block bg-black text-white px-8 md:px-12 py-3 md:py-4 font-bold text-xs md:text-sm uppercase tracking-wider hover:bg-gray-800 transition-all"
              >
                {getContent('hero_cta_text', 'BEGIN YOUR JOURNEY →')}
              </Link>
            </div>
          </motion.div>
        </section>

        {/* Welcome Section */}
        <section data-testid="welcome-section" className="mb-16 md:mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="polaroid tape-corners">
                <img
                  src="https://customer-assets.emergentagent.com/job_tattoo-scrapbook/artifacts/defog9px_IMG_9528.jpeg"
                  alt="Tattoo artist at work"
                  className="w-full h-[300px] sm:h-[400px] object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="paper-texture p-6 md:p-10 tape-scrapbook"
            >
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-medium mb-4 md:mb-6 leading-tight">
                VISION TATTOOS
              </h2>
              <p className="font-body text-base md:text-lg mb-4 md:mb-5 leading-relaxed text-gray-700">
                A private, appointment-only tattoo studio based in Lucknow, created as an intentional alternative to traditional high-volume tattoo spaces.
              </p>
              <p className="font-body text-sm md:text-base text-gray-600 mb-6 md:mb-8 leading-relaxed">
                Fine line • Microrealism • Freehand • Custom Work
              </p>
              <Link
                to="/quote-estimator"
                data-testid="book-session-button"
                className="inline-block bg-black text-white px-6 md:px-10 py-3 md:py-4 font-bold text-xs md:text-sm uppercase tracking-wider hover:bg-gray-800 transition-all"
              >
                BOOK A SESSION →
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Virtual Studio Tour */}
        <section data-testid="studio-tour-section" className="mb-16 md:mb-24">
          <div className="border-t-2 border-black pt-6 md:pt-8 mb-8 md:mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl md:text-6xl font-bold">VIRTUAL STUDIO TOUR</h2>
            <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-gray-600 mt-2">FEEL THE VIBE</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="polaroid tape-effect relative">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/sBE0D-ardTk?autoplay=1&mute=1&loop=1&playlist=sBE0D-ardTk&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1"
                  title="Vision Tattoo Studio Tour"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; loop"
                  allowFullScreen
                  data-testid="studio-tour-video"
                />
              </div>
            </div>
            <p className="font-body text-sm text-gray-600 mt-4 text-center">
              Experience the calm, creative atmosphere of our studio
            </p>
          </motion.div>
        </section>

        {/* About Us Section */}
        <section id="about" data-testid="about-section" className="mb-16 md:mb-24">
          <div className="mb-8 md:mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl md:text-6xl font-bold mb-2">ABOUT US</h2>
            <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-gray-600">OUR STORY</p>
          </div>

          {artist && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16"
            >
              <div className="relative">
                <div className="polaroid tape-top-left">
                  <img
                    src={artist.image_url}
                    alt={artist.name}
                    className="w-full h-[300px] sm:h-[400px] md:h-[500px] object-cover object-center"
                  />
                </div>
                <div className="mt-4">
                  <h3 className="font-heading text-2xl md:text-3xl font-bold">{artist.name}</h3>
                  <p className="font-body text-sm md:text-base text-gray-600">{artist.specialization}</p>
                </div>
              </div>

              <div className="flex flex-col justify-center">
                <p className="font-body text-base md:text-lg leading-relaxed mb-4 md:mb-6">
                  {artist.bio}
                </p>
                <Link
                  to={`/about`}
                  data-testid="view-more-link"
                  className="inline-block bg-black text-white px-6 md:px-8 py-3 font-bold text-xs md:text-sm uppercase tracking-wider hover:bg-gray-800 transition-all w-fit"
                >
                  VIEW MORE →
                </Link>
              </div>
            </motion.div>
          )}
        </section>

        {/* Artist Collaboration Section */}
        <section data-testid="artist-collaboration-section" className="mb-16 md:mb-24">
          <div className="mb-8 md:mb-12 border-t-2 border-black pt-6 md:pt-8">
            <h2 className="font-heading text-3xl sm:text-4xl md:text-6xl font-bold">COLLABORATE WITH US</h2>
            <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-gray-600 mt-2">Creative Partnerships</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="paper-texture p-6 md:p-8 tape-top-left"
            >
              <p className="font-body text-sm md:text-base text-gray-700 mb-6 leading-relaxed">
                We're open to working with artists and creators across different mediums — from tattoo collaborations to showcasing work within our space.
              </p>
              <h3 className="font-heading text-lg md:text-xl font-bold mb-4">Collaboration Types</h3>
              <ul className="space-y-2 mb-6">
                <li className="font-body text-sm md:text-base text-gray-700 flex items-center gap-2">
                  <span className="text-black">•</span> Tattoo collaborations (with illustrators, painters, etc.)
                </li>
                <li className="font-body text-sm md:text-base text-gray-700 flex items-center gap-2">
                  <span className="text-black">•</span> Studio showcases (displaying artwork)
                </li>
                <li className="font-body text-sm md:text-base text-gray-700 flex items-center gap-2">
                  <span className="text-black">•</span> Creative collaborations
                </li>
              </ul>
              <Link 
                to="/collaborate"
                className="inline-block bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-wider hover:bg-gray-800 transition-colors"
                data-testid="collaborate-btn"
              >
                Collaborate With Us
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="paper-texture p-6 md:p-8 tape-top-right"
            >
              <h3 className="font-heading text-xl md:text-2xl font-bold mb-4">Guest Artist Program</h3>
              <p className="font-body text-sm md:text-base text-gray-700 mb-4 leading-relaxed">
                We regularly host talented guest artists from across India and beyond. These limited-time residencies bring fresh perspectives and unique styles to our studio.
              </p>
              <p className="font-body text-sm md:text-base text-gray-700 leading-relaxed">
                Follow us on Instagram to stay updated on upcoming guest spots and booking windows.
              </p>
              <div className="mt-6">
                <a 
                  href="https://www.instagram.com/vision.ttt" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-wider hover:bg-gray-800 transition-colors"
                  data-testid="follow-instagram-btn"
                >
                  Follow Updates
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Flash Gallery Section - Mini Shop */}
        <section data-testid="flash-gallery-section" className="mb-16 md:mb-24">
          <div className="mb-8 md:mb-12 text-center border-t-2 border-b-2 border-black py-3 md:py-4">
            <h2 className="font-heading text-3xl sm:text-4xl md:text-6xl font-bold">FLASH GALLERY</h2>
            <p className="font-mono text-xs uppercase tracking-widest text-gray-500 mt-2">Ready-to-ink designs</p>
          </div>

          {/* Horizontal scrolling container */}
          <div className="relative -mx-4 sm:mx-0">
            <div 
              className="flex gap-4 md:gap-6 overflow-x-auto pb-4 md:pb-6 scrollbar-hide scroll-smooth px-4 sm:px-0"
              style={{ 
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {/* Spacer for left padding */}
              <div className="flex-shrink-0 w-4 md:w-12" />
              
              {flashDesigns.map((flash, index) => (
                <motion.div
                  key={flash.id}
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex-shrink-0 w-64 md:w-72 paper-texture p-4 hover:shadow-xl transition-all hover:-translate-y-2"
                  data-testid={`flash-card-${flash.id}`}
                >
                  <img
                    src={flash.image_url}
                    alt={flash.title}
                    className="w-full h-64 md:h-72 object-cover mb-3"
                  />
                  <h3 className="font-heading text-lg font-medium mb-1 truncate">{flash.title}</h3>
                  <p className="font-mono text-xs uppercase tracking-wider text-gray-600">
                    {flash.style} • {flash.size}
                  </p>
                  <Link
                    to={`/quote-estimator?flash=${flash.id}&design=${encodeURIComponent(flash.title)}`}
                    className="mt-3 block w-full text-center bg-black text-white py-2 font-mono text-xs uppercase tracking-wider hover:bg-gray-800 transition-colors"
                    data-testid={`book-flash-${flash.id}`}
                  >
                    Book This Flash
                  </Link>
                </motion.div>
              ))}
              
              {/* View more card */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: flashDesigns.length * 0.1 }}
                className="flex-shrink-0 w-64 md:w-72"
              >
                <Link
                  to="/gallery"
                  className="h-full min-h-[380px] md:min-h-[420px] flex flex-col items-center justify-center border-2 border-dashed border-gray-400 hover:border-black transition-all group"
                >
                  <ArrowRight size={48} className="text-gray-400 group-hover:text-black transition-colors mb-4" />
                  <span className="font-mono text-sm uppercase tracking-wider text-gray-500 group-hover:text-black transition-colors">
                    View Full Gallery
                  </span>
                </Link>
              </motion.div>
              
              {/* Spacer for right padding */}
              <div className="flex-shrink-0 w-4 md:w-12" />
            </div>
            
            {/* Scroll hint gradient */}
            <div className="absolute right-0 top-0 bottom-6 w-20 bg-gradient-to-l from-[#F8F8F5] to-transparent pointer-events-none" />
          </div>

          <div className="text-center mt-8">
            <p className="font-mono text-xs uppercase tracking-widest text-gray-500">
              ← Scroll to explore →
            </p>
          </div>
        </section>

        {/* Testimonials Section */}
        <section data-testid="testimonials-section" className="mb-16 md:mb-24">
          <div className="mb-8 md:mb-12 border-t-2 border-black pt-6 md:pt-8">
            <h2 className="font-heading text-3xl sm:text-4xl md:text-6xl font-bold">TESTIMONIALS</h2>
          </div>

          {testimonials.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-testid="testimonials-grid">
              {testimonials.map((t, idx) => (
                <motion.div
                  key={t.id || idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  className={`paper-texture p-6 md:p-8 ${idx % 2 === 0 ? 'washi-tape' : 'tape-corner'}`}
                  data-testid={`testimonial-card-${idx}`}
                >
                  <p className="font-handwritten text-xl sm:text-2xl md:text-3xl leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <p className="font-handwritten text-lg md:text-xl text-right">
                    — {t.client_name}
                  </p>
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-8 md:mt-12">
            <a 
              href="https://share.google/mUTZLcfY7ICzKNKa6"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-6 md:px-10 py-3 md:py-4 font-bold text-xs md:text-sm uppercase tracking-wider hover:bg-gray-800 transition-all inline-block"
              data-testid="see-all-reviews-btn"
            >
              SEE ALL REVIEWS →
            </a>
          </div>
        </section>

        {/* Blog Section */}
        <section data-testid="blog-section" className="mb-16 md:mb-24">
          <div className="mb-8 md:mb-12 border-t-2 border-black pt-6 md:pt-8">
            <h2 className="font-heading text-3xl sm:text-4xl md:text-6xl font-bold mb-4 md:mb-6">BLOG</h2>
            <div className="font-body text-sm md:text-base text-gray-600">
              <p>April 2024</p>
              <p>vision.tttlab@gmail.com</p>
              <p className="mt-2">Open Daily | 12 PM — 7 PM</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {blogPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={`/blog/${post.slug}`} data-testid={`blog-preview-${post.id}`}>
                  <div className={`polaroid mb-3 md:mb-4 ${index % 2 === 0 ? 'tape-top-left' : 'tape-top-right'}`}>
                    {post.cover_image && (
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-48 sm:h-56 md:h-64 object-cover"
                      />
                    )}
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold mb-2 hover:underline">
                    {post.title}
                  </h3>
                  <p className="font-body text-xs md:text-sm text-gray-600 mb-2">April 2024</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>

      </div>

      <Footer />
    </div>
  );
};

export default HomePage;